import React, { useMemo, useState } from 'react';
import {
  ConfigProvider,
  Table,
  Input,
  Tooltip,
  Spin,
  Button,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  QrcodeOutlined,
  SearchOutlined,
  LinkOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import 'antd/dist/reset.css';
import './QRCodeListPage.css';
import { useQRCodeList } from '../../hooks/useQRCodeList';

/* ── Helpers ────────────────────────────────────── */

function formatDate(raw) {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return raw;
  }
}

function utmEntries(utmParams) {
  if (!utmParams || typeof utmParams !== 'object') return [];
  return Object.entries(utmParams).filter(([, v]) => v);
}

function shortenKey(key) {
  return key.replace(/^utm_/, '');
}

/* ── Column definitions ─────────────────────────── */

const buildColumns = () => [
  {
    title: 'QR Code',
    key: 'qr',
    width: 180,
    render: (_, row) => (
      <div>
        {row.title
          ? <div className="qrl-title">{row.title}</div>
          : <div className="qrl-no-title">Untitled</div>
        }
        <span className="qrl-id-badge">#{row.id}</span>
      </div>
    ),
  },
  {
    title: 'Slug',
    dataIndex: 'slug',
    key: 'slug',
    width: 130,
    render: (slug) => <span className="qrl-slug">{slug}</span>,
  },
  {
    title: 'Destination URL',
    dataIndex: 'url',
    key: 'url',
    ellipsis: true,
    render: (url) => (
      <Tooltip title={url} placement="topLeft">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="qrl-url"
        >
          <LinkOutlined style={{ marginRight: 4, fontSize: 11 }} />
          {url}
        </a>
      </Tooltip>
    ),
  },
  {
    title: 'Size',
    dataIndex: 'size',
    key: 'size',
    width: 75,
    align: 'center',
    render: (size) => <span className="qrl-size">{size}px</span>,
    sorter: (a, b) => a.size - b.size,
  },
  {
    title: 'Colors',
    key: 'colors',
    width: 130,
    render: (_, row) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="qrl-color-cell">
          <Tooltip title="Foreground">
            <span
              className="qrl-swatch"
              style={{ background: row.color }}
            />
          </Tooltip>
          <span className="qrl-color-hex">{row.color}</span>
        </div>
        <div className="qrl-color-cell">
          <Tooltip title="Background">
            <span
              className="qrl-swatch"
              style={{ background: row.background }}
            />
          </Tooltip>
          <span className="qrl-color-hex">{row.background}</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Logo',
    dataIndex: 'logoEnabled',
    key: 'logoEnabled',
    width: 80,
    align: 'center',
    render: (enabled) =>
      enabled ? (
        <span className="qrl-badge qrl-badge-yes">
          <CheckCircleFilled style={{ fontSize: 10 }} /> On
        </span>
      ) : (
        <span className="qrl-badge qrl-badge-no">Off</span>
      ),
    filters: [
      { text: 'Enabled', value: 1 },
      { text: 'Disabled', value: 0 },
    ],
    onFilter: (value, record) => record.logoEnabled === value,
  },
  {
    title: 'UTM Params',
    dataIndex: 'utmParams',
    key: 'utmParams',
    width: 200,
    render: (utmParams) => {
      const entries = utmEntries(utmParams);
      if (!entries.length) return <span className="qrl-utm-none">—</span>;
      return (
        <div className="qrl-utm-cell">
          {entries.map(([k, v]) => (
            <Tooltip key={k} title={`${k}: ${v}`}>
              <span className="qrl-utm-tag">
                <span className="qrl-utm-key">{shortenKey(k)}</span>
                {v}
              </span>
            </Tooltip>
          ))}
        </div>
      );
    },
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 110,
    render: (date) => <span className="qrl-date">{formatDate(date)}</span>,
    sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    defaultSortOrder: 'descend',
  },
];

/* ── Component ──────────────────────────────────── */

export default function QRCodeListPage() {
  const navigate = useNavigate();
  const { loading, error, list, pagination, page, pageSize, setPage, refetch } = useQRCodeList();
  const [search, setSearch] = useState('');

  // Client-side search filters within the current page only.
  // When search changes, results update immediately without a new API call.
  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        item.slug?.toLowerCase().includes(q) ||
        item.url?.toLowerCase().includes(q) ||
        (item.title || '').toLowerCase().includes(q) ||
        String(item.id).includes(q)
    );
  }, [list, search]);

  const columns = useMemo(() => buildColumns(), []);

  /* ── Body content ── */
  let bodyContent;
  if (loading) {
    bodyContent = (
      <div className="qr-list-state-box">
        <Spin size="default" />
      </div>
    );
  } else if (error) {
    bodyContent = (
      <div className="qr-list-state-box">
        <QrcodeOutlined className="qr-list-state-icon" />
        <span className="qr-list-error-msg">
          {error.message || 'Failed to load QR codes.'}
        </span>
        <Button
          size="small"
          onClick={refetch}
          style={{ fontFamily: 'var(--font-primary)', fontWeight: 600 }}
        >
          Retry
        </Button>
      </div>
    );
  } else {
    bodyContent = (
      <div className="qr-list-table-card">
        <Table
          dataSource={filteredList}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            current:          page,
            pageSize:         pageSize,
            total:            pagination.total ?? 0,
            onChange:         (p, ps) => setPage(p, ps),
            showSizeChanger:  false,
            showTotal:        (total) => `${total} QR code${total !== 1 ? 's' : ''}`,
            hideOnSinglePage: false,
          }}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <div className="qr-list-state-box" style={{ padding: '36px 20px' }}>
                <QrcodeOutlined className="qr-list-state-icon" />
                <span>
                  {search ? 'No results match your search.' : 'No QR codes found.'}
                </span>
              </div>
            ),
          }}
        />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: 'Gabarito, sans-serif',
          colorPrimary: '#3b82f6',
          borderRadius: 8,
          colorBorder: '#e0e7ff',
        },
      }}
    >
      <div className="qr-list-root">
        {/* ── Toolbar ── */}
        <div className="qr-list-toolbar">
          <div className="qr-list-search-wrap">
            <Input
              prefix={<SearchOutlined style={{ color: 'var(--placeholder-text-color)' }} />}
              placeholder="Search by title, URL, slug or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </div>
        </div>

        {/* ── Body ── */}
        <main className="qr-list-body">
          {bodyContent}
        </main>

      </div>
    </ConfigProvider>
  );
}
